import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

import StoryEditorSidebar from "../StoryEditor/Sidebar";
import ContentContainer from "modules/ContentContainer";
import { kioskManagerMapStateToProps } from "mapToProps/kioskManager";
import * as kioskActions from "actions/kiosks";
import "./KioskManager.scss";
import { PublishStatusDialog } from "./PublishStatusDialog";
import SystemReport from "./SystemReport";
import { PublishManager } from "./PublishManager";
import { PublishHistory } from "./PublishHistory";

export class KioskManager extends Component {
  constructor(props) {
    super(props);

    /*
    PublishManager now resolves the relevant publishId locally for
    every banner state (new publish entry while publishing, last
    entry for error/complete) and for History rows the publishId is
    inherent to the row. So the dialog is always opened with a
    concrete publishId -- the previous "awaitingPublishId" mode that
    scanned publishHistory for an in-progress entry is no longer
    needed and has been removed (it had a bug where a previous
    publish's intermediate sub-step entry could be mistaken for the
    new publish's entry, causing the dialog to show the previous
    event's history).
    */
    this.state = {
      isPublishStatusModalOpen: false,
      selectedPublishId: null,
      /*
      Tracks whether a publish is currently in flight on this kiosk,
      reported up from PublishManager (which owns the optimistic
      click state). Lifted here so PublishHistory -- a sibling of
      PublishManager -- can disable its Restore action while a
      publish is happening (concurrent publish/revert operations on
      the same kiosk are not safe on the API side).
      */
      isPublishing: false
    };

    // Poll control for kiosk data refresh
    this._kioskDataInterval = null;

    this.handlePublishCancelClick = this.handlePublishCancelClick.bind(this);
    this.handleViewDetailsClick = this.handleViewDetailsClick.bind(this);
    this.handlePublishingStateChange = this.handlePublishingStateChange.bind(this);
    this.startUptimePolling = this.startUptimePolling.bind(this);
    this.stopUptimePolling = this.stopUptimePolling.bind(this);
    this.handlePublishStatusDialogClose =
      this.handlePublishStatusDialogClose.bind(this);
  }

  handlePublishingStateChange(isPublishing) {
    this.setState({ isPublishing });
  }

  componentDidMount() {
    this.props.requestKiosk();
    this.props.requestProjects();
    this.props.requestKioskAppVersions();
    this.startUptimePolling();
  }

  componentDidUpdate(prevProps, prevState) {
    /*
    If kioskRevertCopy is undefined, this screen has not created a revert snapshot yet.
    When kioskData is defined, the store is populated and a snapshot can be stored.

    If kioskRevertCopy exists but its _id does not match the route kioskID, the user
    navigated to a different kiosk; create a fresh snapshot for the new kiosk.
    */
    if (typeof this.props.kioskRevertCopy === "undefined") {
      if (typeof this.props.kioskData !== "undefined") {
        this.props.createKioskRevertCopy(this.props.kioskID);
      }
    } else {
      if (this.props.kioskID !== this.props.kioskRevertCopy._id) {
        this.props.createKioskRevertCopy(this.props.kioskID);
      }
    }
  }

  componentWillUnmount() {
    this.stopUptimePolling();
  }

  handlePublishCancelClick() {
    const { kioskData, updatePublishHistoryEntry } = this.props;
    
    if (!kioskData) {
      console.warn("Cannot cancel publish: kiosk data not available");
      return;
    }

    // Get the current publish status
    const currentStatus = this.getCurrentPublishStatus();
    
    // Find the most recent publishId from publishHistory that matches the current status
    const publishHistory = kioskData.publishHistory || [];
    const currentEntry = publishHistory
      .filter(entry => entry.status === currentStatus)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

    if (!currentEntry || !currentEntry.publishId) {
      console.warn("Cannot cancel publish: no matching publish history entry found");
      return;
    }

    const metadata = {
      cancelledAt: new Date()
    };

    // Update the publish history entry
    updatePublishHistoryEntry(
      kioskData._id,
      currentEntry.publishId,
      currentEntry.stage,
      currentStatus,
      metadata
    );
  }

  startUptimePolling() {
    // Stop any existing polling first to avoid multiple intervals
    this.stopUptimePolling();

    // Poll kiosk record every 8 seconds (e.g. lastSeenTS / online state)
    this._kioskDataInterval = setInterval(() => {
      this.props.requestKiosk({ lastSeenTS: true });
    }, 8000);
  }

  stopUptimePolling() {
    if (this._kioskDataInterval) {
      clearInterval(this._kioskDataInterval);
      this._kioskDataInterval = null;
    }
  }

  /**
   * Open the publish-status dialog for a specific publishId. Used by
   * both the banner (PublishManager resolves the relevant publishId
   * for the active banner state) and PublishHistory rows (each row
   * has its own publishId).
   * @param {String} publishId
   */
  handleViewDetailsClick = (publishId) => {
    if (!publishId) {
      console.warn("Cannot view details: no publishId provided");
      return;
    }
    this.setState({
      isPublishStatusModalOpen: true,
      selectedPublishId: publishId
    });
  };

  handlePublishStatusDialogClose = () => {
    this.setState({
      isPublishStatusModalOpen: false,
      selectedPublishId: null
    });
  };

  /**
   * Gets the current publish status to display in the dialog. Falls
   * back to the kiosk's top-level publishStatus when no specific
   * publishId is selected (defensive: with the simplified flow the
   * dialog is always opened with a publishId).
   * @returns {String} The publish status to display
   */
  getCurrentPublishStatus() {
    const { kioskData } = this.props;
    const { selectedPublishId } = this.state;

    if (selectedPublishId && kioskData && kioskData.publishHistory) {
      const publishHistory = kioskData.publishHistory;
      const entriesForPublishId = publishHistory.filter(entry => entry.publishId === selectedPublishId);
      if (entriesForPublishId.length > 0) {
        const sortedEntries = entriesForPublishId.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        return sortedEntries[sortedEntries.length - 1].status;
      }
    }

    return kioskData ? kioskData.publishStatus : "preparing";
  }

  /**
   * Gets the publish history entries for a specific publishId
   * @param {String} publishId - The publishId to get history for
   * @returns {Array} Array of history entries for the publishId
   */
  getPublishHistoryForId(publishId) {
    const { kioskData } = this.props;
    if (!publishId || !kioskData || !kioskData.publishHistory) {
      return [];
    }
    return kioskData.publishHistory
      .filter(entry => entry.publishId === publishId)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }

  render() {
    const {
      kioskData,
      section,
      userPreferences,
      userPreferencesSelector,
      sidebarProps,
      sidebarMode,
      kioskType,
      settings,
      allProjects
    } = this.props;

    const SidebarActionBar = (
      <div>
        <div className="story-type-container">
          {kioskData ? kioskData.title : ""}
        </div>
      </div>
    );

    let sidebarActions = null;
    if (section === "settings") {
      sidebarActions = {
        handleInputChange: (prop, value, isLocalized) =>
          this.props.handleSettingsInputChange(prop, value, isLocalized),
        handleSave: () => {
          this.props.createKioskRevertCopy();
          this.props.saveKiosk("settings");
        },
        handleRevert: () => this.props.revertKioskChanges(),
        changeLanguage: value => this.props.changeLanguage(value),
        languages: this.props.languages,
        langID: this.props.langID,
        saveLabel: "Save",
        showButtons: true
      };
    }

    const topBar = (
      <div
        className={`status-indicator ${
          kioskData && kioskData.isOnline ? "online" : "offline"
        }`}
      >
        <div className="icon" />
        <div className="label">
          {kioskData && kioskData.isOnline ? "Online" : "Offline"}
        </div>
      </div>
    );

    let sidebarChildren;
    if (section === "systemReport") {
      sidebarChildren = null;
    } else if (section === "settings") {
      sidebarChildren = null;
    }

    const className =
      section === "systemReport"
        ? "kiosk-system-report"
        : section === "settings"
        ? "kiosk-settings"
        : "kiosk-manager";

    return (
      <div className={`cms-screen ${className}`}>
        <Helmet>
          <title>
            {section === undefined
              ? "Manage Kiosk"
              : section === "systemReport"
              ? "Kiosk: System Report"
              : section === "settings"
              ? "Kiosk: Settings"
              : "Kiosk: TBD"}
          </title>
        </Helmet>

        <StoryEditorSidebar
          {...sidebarProps}
          actionBar={SidebarActionBar}
          {...sidebarActions}
        >
          {sidebarChildren}
        </StoryEditorSidebar>

        <ContentContainer
          className="content-container"
          sidebarMode={sidebarMode}
          topBarAlignContentRight={false}
          topBar={topBar}
        >
          {section === "systemReport" ? (
            <SystemReport
              kioskData={kioskData}
              kioskType={kioskType}
              settings={settings}
            />
          ) :
          section === "settings" ? (
            null
          ) : (
            <div>
              <PublishManager
                projects={allProjects}
                kioskData={kioskData}
                handleSettingsInputChange={this.props.handleSettingsInputChange}
                publishKioskProject={this.props.publishKioskProject}
                requestKiosk={this.props.requestKiosk}
                handlePublishCancelClick={this.handlePublishCancelClick}
                handleViewDetailsClick={this.handleViewDetailsClick}
                onPublishingStateChange={this.handlePublishingStateChange}
              />
              <PublishHistory
                kioskData={kioskData}
                projects={allProjects}
                handleViewDetailsClick={this.handleViewDetailsClick}
                onRevertClick={this.props.revertKioskBundle}
                isPublishingInProgress={this.state.isPublishing}
              />
            </div>
          )}
        </ContentContainer>

        <PublishStatusDialog
          isOpen={this.state.isPublishStatusModalOpen}
          publishStatus={this.getCurrentPublishStatus()}
          publishId={this.state.selectedPublishId}
          publishHistory={this.state.selectedPublishId ? this.getPublishHistoryForId(this.state.selectedPublishId) : null}
          onClose={this.handlePublishStatusDialogClose}
        />
      </div>
    );
  }
}

KioskManager.defaultProps = {
  isLoading: false
};

KioskManager.propTypes = {
  isLoading: PropTypes.bool
};

function mapStateToProps(state, ownProps) {
  return kioskManagerMapStateToProps(state, ownProps);
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  const { kioskID, username } = stateProps;

  return Object.assign({}, stateProps, {
    requestProjects: () => dispatchProps.requestProjects(username),
    requestKiosk: filters =>
      dispatchProps.kioskFetchRequest(kioskID, filters || null),
    publishKioskProject: () => dispatchProps.kioskPublishRequest(kioskID),
    handleSettingsInputChange: (prop, value) => {
      dispatchProps.setKioskProp(kioskID, prop, value);
    },
    saveKiosk: saveType => dispatchProps.kioskUpdateRequest(kioskID, saveType),
    createKioskRevertCopy: () =>
      dispatchProps.createKioskRevertCopy(kioskID),
    revertKioskChanges: () => dispatchProps.revertKioskChanges(),
    updatePublishHistoryEntry: (
      targetKioskID,
      publishID,
      stage,
      status,
      metadata
    ) => {
      dispatchProps.kioskUpdateHistoryEntry(
        targetKioskID,
        publishID,
        stage,
        status,
        metadata
      );
    },
    revertKioskBundle: bundleID =>
      dispatchProps.kioskRevertRequest(kioskID, bundleID),
    requestKioskAppVersions: () =>
      dispatchProps.kioskAppVersionsFetchRequest()
  });
}

export default connect(
  mapStateToProps,
  kioskActions,
  mergeProps
)(KioskManager);
